import React, { useEffect, useState, useCallback } from 'react'
import DocumentHeader from '../../../../documents/components/DocumentHeader'
import { request } from '../../../../../../utils/request';
import { useForm } from 'react-hook-form';
import { FormProvider } from 'react-hook-form';
import { Box, ToggleButton, ToggleButtonGroup, Card, Typography, Alert } from '@mui/material';
import { useNavigate, useParams } from 'react-router-dom';
import InvoiceDatesSection from '../../../../documents/components/InvoiceDatesSection';
import NotesTermsSection from '../../../../documents/components/NotesTermsSection';
import AmountSummarySection from '../../../../documents/components/AmountSummarySection';
import ItemsTableSection from '../../../../documents/components/ItemsTableSection';
import { GstType, DebitNoteFormData, TaxBreakdownItem } from '../../../../types';
import { useGstMode } from '../../../../../../utils/useGstMode';
import { toast } from 'react-toastify';
import { useFinancialYear } from '../../../../../../utils/useFinancialYear';
import { useSearchParams } from 'react-router-dom';
import VendorSelectSection from '../../../../documents/components/VendorSelectSection';

const CreateDebitNote = () => {
  const [searchParams] = useSearchParams();
  const financialYear = useFinancialYear();
  const defaultDebitNoteNumber = `DN${financialYear}-1`;
  const navigate = useNavigate();
  const { gstEnabled } = useGstMode();
  const { id } = useParams();
  const isEditMode = !!id;

  const [taxableAmount, setTaxableAmount] = useState(0);
  const [totalAmount, setTotalAmount] = useState(0);
  const [receivedAmount, setReceivedAmount] = useState(0);
  const [, setTotalTax] = useState(0);
  const [, setTotalDiscount] = useState(0);
  const [, setItems] = useState<any[]>([]);
  const [taxBreakdown, setTaxBreakdown] = useState<TaxBreakdownItem[]>([]);
  const [nextDebitNoteNumber, setNextDebitNoteNumber] = useState(defaultDebitNoteNumber);
  const [isLoadingDebitNoteNumber, setIsLoadingDebitNoteNumber] = useState(false);
  const [loading, setLoading] = useState(false);

  const queryGstType = searchParams.get('gstType');
  const typeName = 'DEBIT_NOTE'

  // Determine default GST type based on query parameter or gstEnabled
  const getDefaultGstType = (): GstType => {
    if (queryGstType === 'NON-GST') {
      return 'NON-GST';
    }
    return gstEnabled ? 'GST' : 'GST';
  };

  const methods = useForm<DebitNoteFormData>({
    defaultValues: {
      debitNoteType: typeName,
      gstType: getDefaultGstType(), // Use the function to get default
      party: '',
      vendor: '',
      paymentType: 'CASH',
      purchaseId: '',
      debitNoteNumber: '',
      debitNoteDate: new Date().toISOString().split('T')[0],
      date: new Date().toISOString().split('T')[0],
      items: [],
      charges: [],
      discount: undefined,
      notes: '',
      terms: '',
      roundOff: false,
      totalAmount: 0,
      taxableAmount: 0,
      subtotal: 0,
      receivedAmount: 0,
      balanceAmount: 0,
      taxBreakdown: [],
    },
    mode: 'onChange'
  });

  const {
    handleSubmit,
    formState: { errors, isSubmitting },
    watch,
    setValue,
    getValues,
    reset
  } = methods;

  // Watch gstType value
  const gstType = watch('gstType', getDefaultGstType());
  const debitNoteType = watch('debitNoteType', typeName);
  const selectedPartyId = watch('party');
  const selectedVendorId = watch('vendor');
  const selectedPurchaseId = watch('purchaseId');

  // Function to fetch next sales return number from API
  const fetchNextDebitNoteNumber = useCallback(async () => {
    if (isEditMode) return;

    setIsLoadingDebitNoteNumber(true);
    try {
      const res = await request({
        url: 'debitNote/get-next-debit-note-number',
        method: "get",
        params: { debitNoteType: debitNoteType, gstType: gstType }
      });

      if (res.success && res.debitNoteNumber) {
        const newDebitNoteNumber = res.debitNoteNumber;
        setNextDebitNoteNumber(newDebitNoteNumber);
        setValue('debitNoteNumber', newDebitNoteNumber);
      }
    } catch (error) {
      console.error("Error fetching next Debit note number:", error);
      // Fallback: Use default
      setNextDebitNoteNumber(defaultDebitNoteNumber);
      setValue('debitNoteNumber', defaultDebitNoteNumber);
    } finally {
      setIsLoadingDebitNoteNumber(false);
    }
  }, [gstType, debitNoteType, isEditMode, setValue, defaultDebitNoteNumber]);

  // Function to fetch existing sales return data for edit mode
  const fetchDebitNoteData = useCallback(async () => {
    if (!isEditMode) return;

    setLoading(true);
    try {
      const res = await request({
        url: `debitNote/get-single-debit-note/${id}`,
        method: "get"
      });

      if (res.success && res.data) {
        const debitNoteData = res.data;

        // Format dates to YYYY-MM-DD
        const formatDate = (dateString: string | Date) => {
          if (!dateString) return new Date().toISOString().split('T')[0];
          const date = new Date(dateString);
          return date.toISOString().split('T')[0];
        };

        // Get party ID - handle both object and string
        let partyId = '';
        let vendorId = '';
        if (typeof debitNoteData.party === 'object' && debitNoteData.party !== null) {
          partyId = debitNoteData.party._id || '';
        } else if (typeof debitNoteData.party === 'string') {
          partyId = debitNoteData.party;
        }

        if (typeof debitNoteData.vendor === 'object' && debitNoteData.vendor !== null) {
          vendorId = debitNoteData.vendor._id || '';
        } else if (typeof debitNoteData.vendor === 'string') {
          vendorId = debitNoteData.vendor;
        }

        // Prepare form data
        const formData = {
          debitNoteType: debitNoteData.debitNoteType || typeName,
          gstType: debitNoteData.gstType || 'GST',
          paymentType: debitNoteData.paymentType,
          party: partyId || '',
          vendor: vendorId || '',
          purchaseId: debitNoteData.purchaseId,
          debitNoteNumber: debitNoteData.debitNoteNumber || '',
          debitNoteDate: formatDate(debitNoteData.debitNoteDate),
          date: formatDate(debitNoteData.date),
          dueDate: formatDate(debitNoteData.dueDate),
          paymentTerms: debitNoteData.paymentTerms || '',
          items: debitNoteData.items || [],
          charges: debitNoteData.charges || [],
          discount: debitNoteData.discount || undefined,
          notes: debitNoteData.notes || '',
          terms: debitNoteData.terms || '',
          roundOff: debitNoteData.roundOff || false,
          totalAmount: debitNoteData.totalAmount || 0,
          taxableAmount: debitNoteData.taxableAmount || 0,
          subtotal: debitNoteData.subtotal || 0,
          receivedAmount: debitNoteData.receivedAmount || 0,
          balanceAmount: debitNoteData.balanceAmount || 0,
          taxBreakdown: debitNoteData.taxBreakdown || [],
        };

        // Reset form with fetched data
        reset(formData);

        // Update state values
        setTaxableAmount(debitNoteData.taxableAmount || 0);
        setTotalAmount(debitNoteData.totalAmount || 0);
        setTotalTax(0); // You might need to calculate this from items
        setTotalDiscount(0); // You might need to calculate this from items
        setItems(debitNoteData.items || []);
        setTaxBreakdown(debitNoteData.taxBreakdown || []);

      } else {
        toast.error(res.message || "Failed to load Debit note data");
      }
    } catch (error: any) {
      console.error("Error fetching Debit note data:", error);
      toast.error(error?.message || "Failed to load Debit note data");
    } finally {
      setLoading(false);
    }
  }, [id, isEditMode, reset]);

  // Function to update all items tax to "none" when NON-GST is selected
  const updateItemsTaxToNone = useCallback(() => {
    const currentItems = getValues('items') || [];
    if (currentItems.length === 0) return;

    const updatedItems = currentItems.map((item: any) => ({
      ...item,
      tax: {
        type: 'none',
        rate: 0
      },
      amount: ((item.quantity || 1) * (item.price || 0)) - (item.discount?.amount || 0)
    }));

    setItems(updatedItems);
    setValue('items', updatedItems);
    setTaxBreakdown([]);
    setValue('taxBreakdown', []);
  }, [getValues, setValue]);

  // Effect to handle query parameter for GST type (only for create mode)
  useEffect(() => {
    if (!isEditMode && queryGstType === 'NON-GST') {
      setValue('gstType', 'NON-GST');
      // Update items tax to none for NON-GST
      updateItemsTaxToNone();
    }
  }, [isEditMode, queryGstType, setValue, updateItemsTaxToNone]);

  // Initial data fetch
  useEffect(() => {
    if (isEditMode) {
      fetchDebitNoteData();
    } else {
      fetchNextDebitNoteNumber();
    }
  }, [isEditMode, fetchDebitNoteData, fetchNextDebitNoteNumber]);

  // Update next sales return number when sales return type changes (only for create mode)
  useEffect(() => {
    if (!isEditMode) {
      fetchNextDebitNoteNumber();
    }
  }, [debitNoteType, isEditMode, fetchNextDebitNoteNumber]);

  const requestWrapper = ({ url, method, params }: { url: string; method: string; params?: any }) => {
    return request({ method: method as any, url, params });
  }

  // Handle GST type change
  const handleGstTypeChange = (event: React.MouseEvent<HTMLElement>, newGstType: GstType | null) => {
    if (newGstType !== null) {
      setValue('gstType', newGstType);
      if (newGstType === 'NON-GST') {
        updateItemsTaxToNone();
      }
    }
  };

  // Effect to handle GST type changes
  useEffect(() => {
    if (gstType === 'NON-GST') {
      updateItemsTaxToNone();
    }
  }, [gstType, updateItemsTaxToNone]);

  // Add this useEffect right after your other useEffect hooks
  useEffect(() => {
    const fetchInvoiceDetails = async () => {
      if (!selectedPurchaseId) {
        // Clear the form if no invoice is selected
        setValue('items', []);
        setTaxableAmount(0);
        setTotalAmount(0);
        setReceivedAmount(0);
        setTaxBreakdown([]);
        return;
      }

      try {
        const res = await request({
          url: `purchase/get-single-purchase-invoice/${selectedPurchaseId}`,
          method: 'get'
        });

        if (res.success && res.data) {
          const invoiceData = res.data;

          // Auto-fill items from the selected invoice
          if (invoiceData.items && invoiceData.items.length > 0) {
            // Transform invoice items to sales return items
            const itemsToAdd = invoiceData.items.map((item: any, index: any) => ({
              id: item.id,
              srNo: index + 1,
              productId: item.productId,
              itemName: item.product?.itemName || item.itemName || '',
              description: item.description || '',
              quantity: item.quantity || 1,
              price: item.price || 0,
              amount: item.amount || 0,
              discount: item.discount || { type: 'none', amount: 0 },
              tax: item.tax || { type: 'none', rate: 0 },
              hsnNo: item.hsnNo || '',
            }));

            // Update items in the form
            setItems(itemsToAdd);
            setValue('items', itemsToAdd);

            // Update taxable amount if GST invoice
            if (invoiceData.taxableAmount) {
              setTaxableAmount(invoiceData.taxableAmount);
              setValue('taxableAmount', invoiceData.taxableAmount);
            }

            // Update total amount
            if (invoiceData.totalAmount) {
              setTotalAmount(invoiceData.totalAmount);
              setValue('totalAmount', invoiceData.totalAmount);
            }

            // Update received amount
            // if (invoiceData.receivedAmount) {
            //   setReceivedAmount(invoiceData.receivedAmount);
            //   setValue('receivedAmount', invoiceData.receivedAmount);
            // }

            // Update charges
            if (invoiceData.charges) {
              // setReceivedAmount(invoiceData.receivedAmount);
              setValue('charges', invoiceData.charges);
            }

            // Update discount
            if (invoiceData.discount) {
              // setReceivedAmount(invoiceData.receivedAmount);
              setValue('discount', invoiceData.discount);
            }

            if (invoiceData?.paymentType) {
              setValue('paymentType', invoiceData.paymentType);
            }

            // Update tax breakdown if GST
            if (invoiceData.taxBreakdown && gstType === 'GST') {
              setTaxBreakdown(invoiceData.taxBreakdown);
              setValue('taxBreakdown', invoiceData.taxBreakdown);
            }
          } else {
            console.log("❌ No items found in invoice");
          }

          // Auto-fill notes with reference to the invoice
          if (invoiceData.notes) {
            const currentNotes = getValues('notes') || '';
            const invoiceReference = `Debit note created against Invoice: ${invoiceData.invoiceNumber}\n${invoiceData.notes}`;

            if (!currentNotes.includes(invoiceData.notes)) {
              setValue('notes', `${currentNotes ? currentNotes + '\n\n' : ''}${invoiceReference}`);
            }
          }
          toast.success(`Invoice ${invoiceData.invoiceNumber} details loaded into Debit note`);
        } else {
          toast.error('Failed to load invoice details');
        }
      } catch (error) {
        toast.error('Failed to load invoice details');
      }
    };

    // Only fetch if we're in create mode (not edit mode)
    if (!isEditMode) {
      fetchInvoiceDetails();
    }
  }, [selectedPurchaseId, setValue, getValues, gstType, isEditMode, setItems, setTaxableAmount, setTotalAmount, setTaxBreakdown]);

  const onSubmit = async (data: DebitNoteFormData) => {

    try {
      // Prepare the payload
      const payload = {
        ...data,
        party: data.party && data.party.trim() !== '' ? data.party : null,
        vendor: data.vendor && data.vendor.trim() !== '' ? data.vendor : null,
        purchaseId: data.purchaseId || undefined,
        debitNoteNumber: isEditMode ? data.debitNoteNumber : (data.debitNoteNumber || undefined),
        receivedAmount: data.receivedAmount
      };

      const apiUrl = isEditMode
        ? `debitNote/update-debit-note/${id}`
        : 'debitNote/create-debit-note';
      const method = isEditMode ? 'put' : 'post';

      const res = await request({
        url: apiUrl,
        method: method,
        data: payload
      });

      if (res.success) {
        const successMessage = isEditMode
          ? "Debit note Updated Successfully"
          : "Debit note Created Successfully";
        toast.success(res.message || successMessage);

        // Navigate to sales return list
        navigate('/accounts/purchases/debitNote');
      } else {
        toast.error(res.message || "Operation failed");
      }
    } catch (error: any) {
      console.error("Error submitting debit note:", error);
      toast.error(error || 'Something went wrong');
    }
  }

  const handleCancel = () => {
    navigate('/accounts/purchases/debitNote');
  }

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '50vh' }}>
        <Typography>Loading debit note data...</Typography>
      </Box>
    );
  }

  return (
    <>
      <DocumentHeader
        heading={isEditMode ? 'Edit Debit Note' : 'Create Debit Note'}
        showBackButton
        showCancelButton={true}
        showSaveButton={true}
        onCancel={handleCancel}
        onSave={handleSubmit(onSubmit)}
        saveButtonText={isEditMode ? 'Update Debit Note' : 'Create Debit Note'}
        saveDisabled={isSubmitting}
      />

      {/* GST Type Selection Section */}
      {!gstEnabled && (
        <Card sx={{
          mb: 2,
          p: 2,
          borderRadius: 1,
          boxShadow: 1
        }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
            <Typography variant="body1" fontWeight="medium">
              Debit Note Type:
            </Typography>

            <ToggleButtonGroup
              value={gstType}
              exclusive
              onChange={handleGstTypeChange}
              aria-label="GST Type"
              size="small"
              color="primary"
              disabled={isEditMode} // Disable in edit mode
            >
              <ToggleButton value="GST" aria-label="GST">
                GST
              </ToggleButton>
              <ToggleButton value="NON-GST" aria-label="NON-GST">
                Non-GST
              </ToggleButton>
            </ToggleButtonGroup>

            <Typography variant="caption" color={gstType === 'GST' ? "success.main" : "info.main"}>
              {gstType === 'GST'
                ? 'Includes GST tax calculations'
                : 'No GST tax calculations'}
            </Typography>
          </Box>
          {isEditMode && (
            <Alert severity="info" sx={{ mt: 1, fontSize: '0.875rem' }}>
              GST type cannot be changed for existing urchase returns.
            </Alert>
          )}
        </Card>
      )}

      {/* Form Provider */}
      <FormProvider {...methods}>
        <Box display={"flex"} flexDirection={"column"} gap={1}>
          <Box display={'flex'} gap={1} sx={{ alignItems: 'stretch' }}>
            <Box sx={{ flex: 1, height: 'auto' }}>
              <VendorSelectSection
                request={requestWrapper}
                apiParams={{ status: 'active' }}
                billToLabel="Bill From"
                partyFormName="party"
                vendorFormName="vendor"
                totalPayableLabel='Total Payable'
                onAddNewVendor={() => navigate('/vendors/vendor/addVendor')}
                showTotalDue={true}
                compact={true}
              />

              {errors.vendor && (
                <Typography color="error" variant="caption" sx={{ mt: 1, display: 'block' }}>
                  Please select a party/vendor
                </Typography>
              )}
            </Box>
            <Box sx={{ flex: 1 }}>
              <InvoiceDatesSection
                invoiceNumberLabel="Debit Note No"
                invoiceDateLabel="Debit Note Date"
                formFieldNames={{
                  invoiceNumber: 'debitNoteNumber',
                  invoiceDate: 'debitNoteDate',
                  invoiceId: 'purchaseId'
                }}
                partyId={selectedPartyId}
                vendorId={selectedVendorId}
                isPurchase={true}
                gstType={gstType}
                autoGenerateInvoiceNumber={!isEditMode}
                nextInvoiceNumber={nextDebitNoteNumber}
                showPaymentTerms={false}
                showDueDate={false}
                compact={true}
                isInvoiceLink={true}
                disabled={isLoadingDebitNoteNumber || isEditMode}
                onInvoiceDateChange={() => { }}
                onPaymentTermsChange={() => { }}
                onDueDateChange={() => { }}
                paymentTermsOptions={[]}
              />
              {isLoadingDebitNoteNumber && !isEditMode && (
                <Typography variant="caption" color="text.secondary">
                  Loading next debit note number...
                </Typography>
              )}
              {nextDebitNoteNumber && !isEditMode && (
                <Typography variant="caption" color="primary">
                  Next available number: {nextDebitNoteNumber}
                </Typography>
              )}
            </Box>
          </Box>

          {/* Items Table Section */}
          <Box>
            <ItemsTableSection
              formFieldName="items"
              onItemsChange={(updatedItems) => {
                setItems(updatedItems);
                setValue('items', updatedItems);
              }}
              onSubtotalChange={() => { }}
              onTotalDiscountChange={(discount) => {
                setTotalDiscount(discount);
              }}
              onTotalTaxChange={(tax) => {
                setTotalTax(tax);
              }}
              onTaxableAmountChange={(taxable) => {
                setTaxableAmount(taxable);
                setValue('taxableAmount', taxable);
              }}
              onTotalAmountChange={(total) => {
                setTotalAmount(total);
                setValue('totalAmount', total);
              }}
              onTaxBreakdownChange={(breakdown) => {
                if (gstType === 'GST') {
                  setTaxBreakdown(breakdown);
                  setValue('taxBreakdown', breakdown);
                }
              }}
              showHsnNo={gstType === 'GST'}
              showDiscount={true}
              isPurchase={false}
              showTax={gstType === 'GST'}
              disabled={false}
              compact={true}
              maxItems={100}
            // Items will be automatically populated from the form
            />
          </Box>

          <Box display={'flex'} gap={1} sx={{ alignItems: "stretch" }}>
            <Box sx={{ flex: 1 }}>
              <NotesTermsSection
                notesLabel="Additional Notes"
                termsLabel="Terms & Conditions"
                addNoteButtonText="Add Note"
                addTermsButtonText="Add Terms"
                formFieldNames={{
                  notes: 'notes',
                  terms: 'terms'
                }}
                showNotes={true}
                showTerms={true}
                allowMultipleNotes={false}
                allowMultipleTerms={false}
                compact={true}
                maxNotesLength={500}
                maxTermsLength={1000}
                onNotesChange={() => { }}
                onTermsChange={() => { }}
                notesPlaceholder="Add any special instructions, delivery notes, or additional information..."
                termsPlaceholder="Add payment terms, return policies, warranties, or other conditions..."
                defaultExpanded={!!getValues('notes') || !!getValues('terms')}
              />
            </Box>
            <Box sx={{ flex: 1 }}>
              <AmountSummarySection
                taxableAmount={taxableAmount}
                totalAmount={totalAmount}
                taxBreakdown={gstType === 'GST' ? taxBreakdown : []}
                receivedAmount={receivedAmount}
                receivedAmountLabel={'Paid Amount'}
                formFieldNames={{
                  charges: 'charges',
                  discount: 'discount',
                  roundOff: 'roundOff',
                  totalAmount: 'totalAmount',
                  taxableAmount: 'taxableAmount',
                  subtotal: 'subtotal',
                  paymentType: 'paymentType',
                  receivedAmount: 'receivedAmount',
                  taxBreakdown: 'taxBreakdown',
                  balanceAmount: 'balanceAmount'
                }}
                compact={true}
                disabled={false}
                taxRate={gstType === 'GST' ? 18 : 0}
                showTaxableAmount={gstType === 'GST'}
                onTotalAmountChange={() => { }}
              />
            </Box>
          </Box>
        </Box>
      </FormProvider>
    </>
  )
}

export default CreateDebitNote;